/**
 * Token Statistics Service
 * Tracks token usage per model/phase for cost analysis
 *
 * Key insight: Newer models (Opus 4.5) can be CHEAPER despite higher per-token price
 * because they produce 48-65% fewer tokens for the same task quality.
 *
 * Sources:
 * - Opus 4.5 uses up to 65% fewer tokens (ampcode.com)
 * - A $1.00 task on Sonnet can cost $0.75 on Opus (cosmicjs.com)
 */

// Pricing per 1M tokens (December 2025)
export const TOKEN_PRICING = {
  claude: {
    'opus': { input: 5, output: 25, efficiency: 0.48 },      // 48% fewer tokens at medium effort
    'opus-4.5': { input: 5, output: 25, efficiency: 0.48 },
    'sonnet': { input: 3, output: 15, efficiency: 1.0 },     // Baseline
    'sonnet-4.5': { input: 3, output: 15, efficiency: 1.0 },
    'haiku': { input: 0.25, output: 1.25, efficiency: 1.2 }, // May be verbose
  },
  openai: {
    'gpt-5.1': { input: 5, output: 20, efficiency: 0.7 },
    'gpt-5': { input: 5, output: 15, efficiency: 0.8 },
    'gpt-4o': { input: 2.5, output: 10, efficiency: 1.0 },   // Baseline
    'gpt-4o-mini': { input: 0.15, output: 0.6, efficiency: 1.3 },
  },
  gemini: {
    'pro': { input: 1.5, output: 6, efficiency: 0.85 },
    'deep-think': { input: 2, output: 8, efficiency: 0.6 },  // Most efficient
    'flash': { input: 0.075, output: 0.3, efficiency: 1.0 },
    'flash-lite': { input: 0.04, output: 0.15, efficiency: 1.4 },
  },
}

// Session stats storage
let sessionStats = {
  byPhase: {},
  byModel: {},
  total: {
    inputTokens: 0,
    outputTokens: 0,
    cost: 0,
    calls: 0,
  },
  startTime: null,
}

/**
 * Reset session stats
 */
export function resetStats() {
  sessionStats = {
    byPhase: {},
    byModel: {},
    total: {
      inputTokens: 0,
      outputTokens: 0,
      cost: 0,
      calls: 0,
    },
    startTime: Date.now(),
  }
}

/**
 * Calculate cost for token usage
 */
export function calculateCost(provider, model, inputTokens, outputTokens) {
  const pricing = TOKEN_PRICING[provider]?.[model]
  if (!pricing) {
    // Fallback to provider defaults
    const defaultPricing = { input: 3, output: 15 }
    return (inputTokens * defaultPricing.input + outputTokens * defaultPricing.output) / 1_000_000
  }

  return (inputTokens * pricing.input + outputTokens * pricing.output) / 1_000_000
}

/**
 * Calculate "effective cost" accounting for token efficiency
 * A more efficient model may cost less for the same task quality
 */
export function calculateEffectiveCost(provider, model, baseOutputTokens) {
  const pricing = TOKEN_PRICING[provider]?.[model]
  if (!pricing) return null

  // Adjust output tokens by efficiency factor
  const adjustedOutput = baseOutputTokens * pricing.efficiency
  const avgInput = baseOutputTokens * 0.3 // Rough estimate: input ~30% of output

  return {
    theoretical: calculateCost(provider, model, avgInput, baseOutputTokens),
    effective: calculateCost(provider, model, avgInput, adjustedOutput),
    efficiency: pricing.efficiency,
    savings: pricing.efficiency < 1 ? `${Math.round((1 - pricing.efficiency) * 100)}% fewer tokens` : null,
  }
}

/**
 * Record token usage from an LLM call
 */
export function recordUsage({ provider, model, phase, inputTokens, outputTokens }) {
  if (!sessionStats.startTime) {
    sessionStats.startTime = Date.now()
  }

  const cost = calculateCost(provider, model, inputTokens, outputTokens)
  const modelKey = `${provider}/${model}`

  // Update total
  sessionStats.total.inputTokens += inputTokens
  sessionStats.total.outputTokens += outputTokens
  sessionStats.total.cost += cost
  sessionStats.total.calls += 1

  // Update by phase
  if (phase) {
    if (!sessionStats.byPhase[phase]) {
      sessionStats.byPhase[phase] = {
        inputTokens: 0,
        outputTokens: 0,
        cost: 0,
        calls: 0,
        model: modelKey,
      }
    }
    sessionStats.byPhase[phase].inputTokens += inputTokens
    sessionStats.byPhase[phase].outputTokens += outputTokens
    sessionStats.byPhase[phase].cost += cost
    sessionStats.byPhase[phase].calls += 1
  }

  // Update by model
  if (!sessionStats.byModel[modelKey]) {
    sessionStats.byModel[modelKey] = {
      inputTokens: 0,
      outputTokens: 0,
      cost: 0,
      calls: 0,
    }
  }
  sessionStats.byModel[modelKey].inputTokens += inputTokens
  sessionStats.byModel[modelKey].outputTokens += outputTokens
  sessionStats.byModel[modelKey].cost += cost
  sessionStats.byModel[modelKey].calls += 1

  // Log for debugging
  console.log(`💰 [TOKENS] ${phase || 'unknown'}: ${modelKey} - ${inputTokens}→${outputTokens} tokens = $${cost.toFixed(4)}`)

  return { cost, inputTokens, outputTokens }
}

/**
 * Get current session stats
 */
export function getStats() {
  return {
    ...sessionStats,
    duration: sessionStats.startTime ? Date.now() - sessionStats.startTime : 0,
  }
}

/**
 * Get formatted stats summary
 */
export function getStatsSummary() {
  const stats = getStats()

  return {
    totalCost: `$${stats.total.cost.toFixed(4)}`,
    totalTokens: stats.total.inputTokens + stats.total.outputTokens,
    inputTokens: stats.total.inputTokens,
    outputTokens: stats.total.outputTokens,
    calls: stats.total.calls,
    duration: Math.round(stats.duration / 1000),
    avgTokensPerCall: stats.total.calls > 0
      ? Math.round((stats.total.inputTokens + stats.total.outputTokens) / stats.total.calls)
      : 0,
    costPerPhase: Object.entries(stats.byPhase).map(([phase, data]) => ({
      phase,
      cost: `$${data.cost.toFixed(4)}`,
      tokens: data.inputTokens + data.outputTokens,
      model: data.model,
    })),
    costPerModel: Object.entries(stats.byModel).map(([model, data]) => ({
      model,
      cost: `$${data.cost.toFixed(4)}`,
      tokens: data.inputTokens + data.outputTokens,
      calls: data.calls,
    })),
  }
}

/**
 * Compare theoretical vs actual cost for different model choices
 */
export function compareModelCosts(outputTokens = 10000) {
  const comparisons = []

  for (const [provider, models] of Object.entries(TOKEN_PRICING)) {
    for (const [model, pricing] of Object.entries(models)) {
      const effective = calculateEffectiveCost(provider, model, outputTokens)
      if (effective) {
        comparisons.push({
          model: `${provider}/${model}`,
          theoretical: `$${effective.theoretical.toFixed(4)}`,
          effective: `$${effective.effective.toFixed(4)}`,
          efficiency: `${Math.round(pricing.efficiency * 100)}%`,
          savings: effective.savings,
        })
      }
    }
  }

  // Sort by effective cost
  comparisons.sort((a, b) => parseFloat(a.effective.slice(1)) - parseFloat(b.effective.slice(1)))

  return comparisons
}

/**
 * Get recommendation based on task type
 */
export function getRecommendation(taskType = 'html_generation') {
  const recommendations = {
    html_generation: {
      best: 'claude/opus',
      reason: 'Opus produces 48-65% fewer tokens for structured output, often cheaper than Sonnet',
      alternative: 'gemini/deep-think',
    },
    analysis: {
      best: 'claude/sonnet',
      reason: 'Good balance of quality and cost for reasoning tasks',
      alternative: 'openai/gpt-4o',
    },
    quick_summary: {
      best: 'claude/haiku',
      reason: 'Fast and cheap for simple summarization',
      alternative: 'gemini/flash-lite',
    },
  }

  return recommendations[taskType] || recommendations.html_generation
}

// Store stats in localStorage for persistence across sessions
export function saveStats() {
  try {
    const history = JSON.parse(localStorage.getItem('emd_token_history') || '[]')
    history.push({
      ...sessionStats,
      savedAt: new Date().toISOString(),
    })
    // Keep last 50 sessions
    if (history.length > 50) history.shift()
    localStorage.setItem('emd_token_history', JSON.stringify(history))
  } catch (e) {
    console.warn('Failed to save token stats:', e)
  }
}

export function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem('emd_token_history') || '[]')
  } catch {
    return []
  }
}

export default {
  recordUsage,
  calculateCost,
  calculateEffectiveCost,
  getStats,
  getStatsSummary,
  compareModelCosts,
  getRecommendation,
  resetStats,
  saveStats,
  loadHistory,
  TOKEN_PRICING,
}
